import { useTranslation } from "react-i18next"

import type { SharePointLibrary } from "../types"

import { Folder } from "@/components/icons"

import { NavigationBreadcrumb } from "./navigation-breadcrumb"
import { sortFiles } from "../utils"
import { formatFileDateTime } from "../utils/date-formatting"

interface SharePointLibrariesProps {
  libraries: SharePointLibrary[]
  sortKey: "name" | "modified"
  sortDir: "asc" | "desc"
  selectedSiteName: string
  onSelectLibrary: (library: SharePointLibrary) => void
  onNavigateBack: () => void
}

export function SharePointLibraries({
  libraries,
  sortKey,
  sortDir,
  selectedSiteName,
  onSelectLibrary,
  onNavigateBack,
}: SharePointLibrariesProps) {
  const { t } = useTranslation()

  return (
    <div className="flex h-full flex-col gap-3 px-1 sm:gap-4 sm:px-0">
      {/* Back button and breadcrumb */}
      <NavigationBreadcrumb
        breadcrumb={selectedSiteName}
        canNavigateBack={true}
        onNavigateBack={onNavigateBack}
      />

      <div className="flex flex-shrink-0 items-center gap-2 border-b pb-2">
        <Folder className="text-primary h-4 w-4 sm:h-5 sm:w-5" />
        <h3 className="text-xs font-medium sm:text-base">
          {t("filepicker.document_libraries", { defaultValue: "Document Libraries" })}
        </h3>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="space-y-2 sm:space-y-3">
          {libraries.length > 0 ? (
            sortFiles(
              libraries,
              (l) => (sortKey === "name" ? l.name : l.lastModified || ""),
              sortDir
            ).map((library) => (
              <button
                key={library.driveId}
                className="group bg-card hover:bg-accent/50 focus-visible:ring-ring w-full cursor-pointer rounded-lg border p-3 text-left transition-colors focus-visible:ring-1 focus-visible:outline-none sm:p-4"
                type="button"
                onClick={() => onSelectLibrary(library)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-foreground text-xs font-medium sm:text-sm">
                      {library.name}
                    </h4>
                    {library.lastModified && (
                      <p className="text-muted-foreground mt-1 text-[10px] sm:text-xs">
                        {t("filepicker.modified", { defaultValue: "Modified" })}{" "}
                        {formatFileDateTime(library.lastModified)}
                      </p>
                    )}
                  </div>
                  <Folder className="text-muted-foreground group-hover:text-primary h-5 w-5 transition-colors" />
                </div>
              </button>
            ))
          ) : (
            <div className="flex h-full items-center justify-center py-12">
              <p className="text-muted-foreground text-sm">
                {t("filepicker.no_document_libraries", {
                  defaultValue: "No document libraries available",
                })}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
